import { makeStyles } from '@material-ui/styles'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { customValidator, nameWithHyphenAndUnderscoreValidator } from 'core/utils/fieldValidators'
import { trimInputOnBlur } from 'core/utils/helpers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { GET_DRIVER_CONFIG, getPairValue, waitSeconds } from '../helpers'
import BackendDriverPicklist from './BackendDriverPicklist'
import Text from 'core/elements/Text'
import Alert from 'core/components/Alert'

const useStyles = makeStyles<Theme>((theme) => ({}))

interface Props {
  volumeConfig: any
  currentVolumeConfigs: any[]
  updateVolumeConfigs: (config: any) => void
  onClose: () => void
  volumeType: any
}

const PAIR_TO_REMOVE = ['volume_backend_name']
const requiredKeys = [
  'nfs_shares_config',
  'netapp_server_hostname',
  'netapp_storage_family',
  'netapp_storage_protocol',
  'netapp_vserver',
  'netapp_server_port',
  'netapp_login',
  'netapp_password',
]

const DRIVERS = [
  'NFS',
  'LVM',
  'NetApp',
  'PureStorageISCSI',
  'PureStorageFC',
  'PureStorageNVME',
  'HitachiISCSI',
  'HuaweiISCSI',
  'HuaweiFC',
  'Custom',
  'HPE3PARISCSI',
  'HPE3PARFC',
  'Tintri',
]

const ERROR_MESSAGE = (driver) => ({
  title: 'Driver not supported for production',
  message: `${driver} storage driver is not supported for production use. 
    Use this driver only for testing or evaluation. 
    For production, please select a supported storage driver`,
})

const KEYVALUE_UPDATE_DELAY = 0.01

export default function CreateVolumeBackendConfigurationModal({
  volumeConfig,
  currentVolumeConfigs,
  updateVolumeConfigs,
  onClose,
  volumeType,
}: Props) {
  const [showKeyValues, setShowKeyValues] = useState(true)
  const [defaultDriverKeyValues, setDefaultDriverKeyValues] = useState([])
  const [error, setError] = useState(null)
  const defaultParams = useMemo(
    () =>
      volumeConfig
        ? {
            name: volumeConfig.name,
            driver: volumeConfig.driver,
            // Because create vs update is different, for july need to
            // disable the removal of those required keys during update
            keyValues: volumeConfig.keyValues?.map((pair) =>
              requiredKeys.includes(pair.key)
                ? { ...pair, shouldNotShowDelete: true, disabledKey: true }
                : pair,
            ),
            customDriver: volumeConfig.customDriver,
          }
        : {
            name: '',
            driver: '',
            keyValues: [],
            customDriver: '',
          },
    [volumeConfig?.keyValues],
  )

  // const defaultParams = {
  //   name: '',
  //   driver: '',
  //   keyValues: [],
  // }
  const classes = useStyles()
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const updateKeyValuePair = async (value) => {
    setShowKeyValues(false)
    await waitSeconds(KEYVALUE_UPDATE_DELAY)
    getParamsUpdater('keyValues')(value)
    setShowKeyValues(true)
  }

  const onKeyValueChange = async (value) => {
    let newValue = value
    const hasNfsMountPoint = value.some((pair) => pair.key === 'nfs_mount_points')

    if (params.driver === 'NetApp') {
      if (value.some((pair) => pair.key === 'netapp_storage_protocol' && pair.value === 'nfs')) {
        if (!hasNfsMountPoint) {
          newValue = [...value, { key: 'nfs_mount_points', value: '', shouldNotShowDelete: true }]
          updateKeyValuePair(newValue)
        }
      }
      if (value.some((pair) => pair.key === 'netapp_storage_protocol' && pair.value !== 'nfs')) {
        if (hasNfsMountPoint) {
          newValue = value.filter((pair) => pair.key !== 'nfs_mount_points')
          updateKeyValuePair(newValue)
        }
      }
    }

    if (value.some((pair) => PAIR_TO_REMOVE.includes(pair.key) && pair.value === '')) {
      newValue = value.map((pair) => {
        if (PAIR_TO_REMOVE.includes(pair.key))
          return {
            key: pair.key,
            value: getPairValue(params.name)[pair.key],
            disabledValue: true,
            disabledKey: true,
          }
        return pair
      })
      updateKeyValuePair(newValue)
      return
    }

    getParamsUpdater('keyValues')(newValue)
  }

  const selectedDriver = (driver) => {
    if (!DRIVERS.includes(driver)) {
      return
    }

    const runCheck = async () => {
      if (!!GET_DRIVER_CONFIG[driver]) {
        setShowKeyValues(false)
        await waitSeconds(0.5)
        // updateParams(GET_DRIVER_CONFIG[driver]) this updates the keyValues with default values
        setDefaultDriverKeyValues(GET_DRIVER_CONFIG[driver].keyValues)
        setShowKeyValues(true)
      }
    }
    runCheck()
  }

  const duplicateNameValidator = useMemo(() => {
    return customValidator((value) => {
      if (!value) {
        return true
      }
      if (volumeConfig?.name === value) {
        return true
      }
      return !currentVolumeConfigs.find((config) => config?.name === value)
    }, 'A configuration with that name already exists')
  }, [currentVolumeConfigs])

  const nfsSharesConfigValidator = useMemo(() => {
    return customValidator((value) => {
      if (!value) {
        return true
      }
      // Check for other NFS drivers, if they're on a different volume type
      // then make sure the nfs_shares_config is different
      const nfsConfigsOnOtherVolumeTypes = currentVolumeConfigs?.filter(
        (config) =>
          config?.volumeType !== volumeType?.type && ['NFS', 'NetApp'].includes(config?.driver),
      )
      return !nfsConfigsOnOtherVolumeTypes?.find((config) => {
        const pair = config?.keyValues?.find((keyValue) => keyValue?.key === 'nfs_shares_config')
        return pair?.value === value
      })
    }, 'This NFS share config is already in-use by another volume type')
  }, [currentVolumeConfigs, volumeType?.type])

  // useEffect(() => {
  //   if (volumeConfig) {
  //     updateParams({
  //       ...volumeConfig,
  //     })
  //   }
  // }, [volumeConfig])

  const submitForm = useCallback(async () => {
    const newKeyValuePairs = params.keyValues.filter((pair) => !PAIR_TO_REMOVE.includes(pair.key))
    const keyValuePairs = [...defaultDriverKeyValues, ...newKeyValuePairs]

    const seen = new Set()
    const duplicateKey = keyValuePairs?.find((pair) => {
      if (seen.has(pair?.key)) return true
      seen.add(pair?.key)
      return false
    })?.key

    if (!!duplicateKey) {
      setError({
        title: 'May not have a duplicate key',
        message: `Key ${duplicateKey} is duplicate.`,
      })
      return
    } else {
      setError(null)
    }

    const config = {
      name: params.name,
      driver: params.driver,
      keyValues: keyValuePairs,
      customDriver: params.customDriver,
      volumeType: volumeType.type,
      volumeTypeId: volumeType.id,
    }
    updateVolumeConfigs(config)
    handleClose()
  }, [params, volumeType, defaultDriverKeyValues])

  const handleClose = () => {
    setParams(defaultParams)
    onClose()
  }

  return (
    <ModalForm
      title={`${volumeConfig ? 'Update' : 'Add'} Volume Backend Configuration`}
      onSubmit={submitForm}
      onClose={handleClose}
      // submitting={updating || updatingSubnet}
      // error={error || subnetError}
      submitTitle={`${volumeConfig ? 'Update' : 'Add'} Volume Backend Configuration`}
      open
      error={error}
    >
      <FormFieldSection title="Basic Settings">
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          onBlur={(e) => trimInputOnBlur(e, getParamsUpdater('name'))}
          value={params.name}
          validations={[nameWithHyphenAndUnderscoreValidator, duplicateNameValidator]}
          required
        />
        <PicklistField
          DropdownComponent={BackendDriverPicklist}
          name="driver"
          id="driver"
          label="Storage Driver"
          compact={false}
          value={params.driver}
          onChange={(value) => {
            getParamsUpdater('driver')(value)
            selectedDriver(value)
          }}
          required
        />
        {['LVM', 'NFS'].includes(params.driver) && (
          <Alert
            variant="warning"
            title={ERROR_MESSAGE(params.driver)?.title}
            message={ERROR_MESSAGE(params.driver)?.message}
          />
        )}
        {params.driver === 'Custom' && (
          <TextField
            id="customDriver"
            label="Custom Driver Name"
            onChange={getParamsUpdater('customDriver')}
            onBlur={(e) => trimInputOnBlur(e, getParamsUpdater('customDriver'))}
            value={params.customDriver}
            info="Must be a fully qualified driver name"
            required
          />
        )}
      </FormFieldSection>
      <FormFieldSection title="Key Value Configurations">
        {defaultDriverKeyValues?.map((item, index) => (
          <TextField
            key={item?.key}
            id={item?.key}
            label={`${item?.key} ${item?.required ? '' : '(Optional)'}`}
            required={item?.required}
            value={defaultDriverKeyValues?.[index]?.value}
            placeholder="Value"
            onChange={(value) => {
              const updatedKeyValues = defaultDriverKeyValues?.map((kvItem) => {
                if (kvItem?.key === item?.key) {
                  return { ...kvItem, value: value }
                }
                return { ...kvItem }
              })
              setDefaultDriverKeyValues(updatedKeyValues)
            }}
            validations={item?.key === 'nfs_shares_config' ? [nfsSharesConfigValidator] : null}
            info={item?.tooltip}
            align={{ vertical: 'top', horizontal: 'middle' }}
          ></TextField>
        ))}
        {showKeyValues && (
          <KeyValuesField
            id="keyValues"
            label="Key Values"
            value={params.keyValues}
            onChange={onKeyValueChange}
            addLabel="Add Key/Value Pair"
            allowMultipleValues
          />
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
